import { Component } from '../Component.js';
import { ROUNDS, SCORER_TEAMS, TEAM_NAMES } from '../../config/content.js';
import { fk, escHtml, roundTotal, teamLabel } from '../../utils/helpers.js';

export class ScorerView extends Component {
  /** @type {Record<string, number[]>} locally staged scores, keyed by `${teamKey}_R${r}` */
  #staged = {};

  /** Facilitator/master team filter: 'all' or a canonical team name — see #isOverseer() */
  #teamFilter = 'all';

  /** Currently selected round index, NaN when none selected */
  #selectedRound = NaN;

  constructor(el, { gameStore, localStore, firebase }) {
    super(el, {});
    this.gameStore  = gameStore;
    this.localStore = localStore;
    this.firebase   = firebase;
  }

  /** Facilitators and master can score any registered team, not just a fixed scorer pair. */
  #isOverseer(L) {
    return L.isMaster || L.role === 'facilitator';
  }

  template() {
    const G  = this.gameStore.getState();
    const L  = this.localStore.getState();
    const sn = L.scorerNum ?? 1;
    const roundOpts = ROUNDS.map((R, i) =>
      `<option value="${i}"${this.#selectedRound === i ? ' selected' : ''}>Round ${i + 1} — ${escHtml(R.name)}</option>`
    ).join('');

    let headerHtml, teamSelectHtml = '';
    if (this.#isOverseer(L)) {
      const registered = TEAM_NAMES.filter(t => G.teams[fk(t)]);
      const teamOpts = registered.map(t =>
        `<option value="${escHtml(t)}"${this.#teamFilter === t ? ' selected' : ''}>${escHtml(teamLabel(t, G.teams[fk(t)]?.displayName))}</option>`
      ).join('');
      headerHtml = `<span style="font-size:12px;font-weight:700;color:var(--surface-navy)">Scoring — any registered team</span>`;
      teamSelectHtml = `
        <label for="sc-tSel" class="sr-only">Select team to score</label>
        <select id="sc-tSel" aria-label="Select team to score">
          <option value="all"${this.#teamFilter === 'all' ? ' selected' : ''}>All registered teams</option>
          ${teamOpts}
        </select>`;
    } else {
      const assigned = (SCORER_TEAMS[sn] ?? []).map(t => teamLabel(t, G.teams[fk(t)]?.displayName)).join(', ');
      headerHtml = `<span style="font-size:12px;font-weight:700;color:var(--surface-navy)">Scorer ${sn} — scoring: ${escHtml(assigned)}</span>`;
    }

    return `
      <div class="card" style="padding:8px 12px;display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:0">
        ${headerHtml}
        ${teamSelectHtml}
        <div style="margin-left:auto">
          <label for="sc-rSel" class="sr-only">Select round to score</label>
          <select id="sc-rSel" aria-label="Select round to score">
            <option value="">— select round —</option>${roundOpts}
          </select>
        </div>
      </div>
      <div id="sc-panels" aria-live="polite"><p class="muted" style="padding:8px;font-size:12px">Select a round.</p></div>
    `;
  }

  bindEvents() {
    const sel = this.$('#sc-rSel');
    if (sel) {
      const handler = () => {
        this.#selectedRound = parseInt(sel.value, 10);
        this.#renderPanels(this.#selectedRound);
      };
      sel.addEventListener('change', handler);
      this.addCleanup(() => sel.removeEventListener('change', handler));
    }
    const tSel = this.$('#sc-tSel');
    if (tSel) {
      const tHandler = () => {
        this.#teamFilter = tSel.value;
        if (!isNaN(this.#selectedRound)) this.#renderPanels(this.#selectedRound);
      };
      tSel.addEventListener('change', tHandler);
      this.addCleanup(() => tSel.removeEventListener('change', tHandler));
    }
    // Re-render panels after a game-state re-render if a round was already selected
    if (!isNaN(this.#selectedRound)) this.#renderPanels(this.#selectedRound);
  }

  #renderPanels(r) {
    const panels = document.getElementById('sc-panels');
    if (!panels) return;
    if (isNaN(r)) { panels.innerHTML = '<p class="muted" style="padding:8px;font-size:12px">Select a round.</p>'; return; }

    const G = this.gameStore.getState();
    const L = this.localStore.getState();
    let teams;
    if (this.#isOverseer(L)) {
      const registered = TEAM_NAMES.filter(t => G.teams[fk(t)]);
      teams = this.#teamFilter === 'all' ? registered : registered.filter(t => t === this.#teamFilter);
    } else {
      const sn = L.scorerNum ?? 1;
      teams = SCORER_TEAMS[sn] ?? [];
    }
    panels.innerHTML = teams.length
      ? teams.map(t => this.#buildPanel(t, r)).join('')
      : '<p class="muted" style="padding:8px;font-size:12px">No registered teams to score yet.</p>';
    this.#bindPanelEvents(panels, r);
  }

  #buildPanel(team, r) {
    const G     = this.gameStore.getState();
    const R     = ROUNDS[r];
    const k     = `${fk(team)}_R${r}`;
    const label = teamLabel(team, G.teams[fk(team)]?.displayName);
    const sub   = G.subs[k];
    const sc    = this.#staged[k] ?? G.scores[k] ?? [...R.dims.map(() => 0), 0];
    const unsaved = !!this.#staged[k];

    if (!sub) return `
      <div class="scorer-card">
        <h3>${escHtml(label)} — Round ${r + 1}: ${escHtml(R.name)}</h3>
        <div class="locked-message" role="status">⏳ No submission yet. Scoring unlocks when the team submits.</div>
      </div>`;

    let h = `<div class="scorer-card" data-team="${escHtml(team)}" data-round="${r}"><h3>${escHtml(label)} — Round ${r + 1}: ${escHtml(R.name)}</h3>`;
    if (sub.members?.length) h += `<div style="margin-bottom:8px;padding:6px 10px;background:#f5f0e3;border-radius:var(--radius);font-size:11px;color:var(--brand-primary-dark)"><strong>Team members:</strong> ${sub.members.map(escHtml).join(', ')}</div>`;

    h += `<div style="background:var(--color-slate);padding:10px;border-radius:var(--radius);margin-bottom:10px;font-size:12px;line-height:1.7">`;
    R.fields.forEach(f => { if (sub[f.id]) h += `<p style="margin-bottom:8px"><strong style="color:var(--surface-navy)">${escHtml(f.label)}:</strong><br>${escHtml(sub[f.id])}</p>`; });
    if (sub.bomb) h += `<p style="margin-bottom:8px;background:#fff5f5;padding:6px 8px;border-radius:4px"><strong style="color:var(--color-danger)">⚡ ${escHtml(R.bombField.label)}:</strong><br>${escHtml(sub.bomb)}</p>`;
    R.steward.forEach((q, i) => { if (sub[`sc${i}`]) h += `<p style="margin-bottom:8px;background:#f5f0e3;padding:6px 8px;border-radius:4px"><strong style="color:var(--brand-primary-dark)">Steward ${i+1}: ${escHtml(q)}</strong><br>${escHtml(sub[`sc${i}`])}</p>`; });
    if (sub.sigs?.some(s => s.img)) {
      h += `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:4px">`;
      sub.sigs.forEach(s => { if (s.img) h += `<div style="text-align:center"><div style="font-size:9px;color:var(--color-muted)">${escHtml(s.name)}</div><img src="${s.img}" style="height:28px;border:1px solid var(--color-border);border-radius:3px" alt="${escHtml(s.name)}'s signature"></div>`; });
      h += '</div>';
    }
    h += '</div>';

    h += `<div style="font-size:10px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:var(--color-muted);margin-bottom:6px" role="heading" aria-level="4">Score each dimension (0–3)</div>`;
    [...R.dims, R.bombDim].forEach((dim, i) => {
      const isBomb = i === R.dims.length;
      h += `<div class="dimension-row${isBomb ? ' dimension-row--bomb' : ''}" role="group" aria-label="${escHtml(dim)}">
        <span>${escHtml(dim)}</span>
        <div class="score-buttons">
          ${[0,1,2,3].map(n => `<button class="score-btn${sc[i] === n ? ' score-btn--active' : ''}"
            data-team="${escHtml(team)}" data-round="${r}" data-dim="${i}" data-val="${n}"
            aria-pressed="${sc[i] === n}" aria-label="Score ${n}">${n}</button>`).join('')}
        </div>
      </div>`;
    });

    const tot = roundTotal(sc);
    h += `<div style="margin-top:8px;padding:8px 10px;background:var(--color-slate);border-radius:var(--radius);display:flex;align-items:center;gap:8px">
      <span style="font-size:12px;font-weight:600">Round total:</span>
      <span style="font-size:17px;font-weight:700;color:var(--surface-navy)" aria-live="polite">${tot}</span>
      <span class="muted" style="font-size:11px">/ ${R.max + 3}</span>
      <button class="btn btn--primary btn--sm save-score-btn" data-team="${escHtml(team)}" data-round="${r}" style="margin-left:auto">
        ${unsaved ? '💾 Save *' : 'Save ✓'}
      </button>
    </div></div>`;
    return h;
  }

  #bindPanelEvents(panels, r) {
    // Score buttons
    panels.querySelectorAll('.score-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const { team, dim, val } = btn.dataset;
        const k   = `${fk(team)}_R${r}`;
        const G   = this.gameStore.getState();
        const R   = ROUNDS[r];
        if (!this.#staged[k]) this.#staged[k] = [...(G.scores[k] ?? [...R.dims.map(() => 0), 0])];
        this.#staged[k][parseInt(dim, 10)] = parseInt(val, 10);
        this.#renderPanels(r);
      });
    });

    // Save buttons
    panels.querySelectorAll('.save-score-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const { team, round: rStr } = btn.dataset;
        const rNum = parseInt(rStr, 10);
        const k    = `${fk(team)}_R${rNum}`;
        const G    = this.gameStore.getState();
        const R    = ROUNDS[rNum];
        const sc   = this.#staged[k] ?? G.scores[k] ?? [...R.dims.map(() => 0), 0];
        btn.disabled = true;
        try {
          await this.firebase.writeGame(`scores/${k}`, sc);
          delete this.#staged[k];
          this.#renderPanels(rNum);
        } catch (e) {
          console.error('[ScorerView] save failed:', e);
          btn.disabled = false;
        }
      });
    });
  }
}
